import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import AsyncSelect from "react-select/lib/Async";
import { NotificationManager } from "react-notifications";
import {
    NumberField,
    TextField,
} from "../../../../../../Utils/renderField/fieldsHookForms";
import { RenderNumber } from "../../../../../../Utils/renderField/renderReadField";
import { useDebouncedAsyncSelect } from "../../../../../../Utils/Hooks/useDebounce";
import { TIPOS_DESTINO_BODEGA_PT } from "../../../../../../../../utility/constants";
import { CreacionSalidaApi } from "../api/CreacionSalidaApi";
import { formatDisplayNumber } from "../../../../../../../../utility/utils";
import { formatExistenciasLabel } from "../helpers/formatLabel";
import { tipoExistenciaSegunDestino } from "../helpers/filtroExistencias";
import { formatPalletLabel } from "../../../../../ProduccionV2/helpers/formatPallet";
import Badge from "../../../../../../Utils/Badge/Badge";
import NullableValue from "../../../../../../Utils/renderField/nullableValue";
const eliminarIcon = require("assets/img/icons/borrar.png");

export const DetallesSalidaList = (props) => {
    const {
        detallesSalida = [],
        setDetallesSalida,
        tipoDestinoSeleccionado,
        almacenesReprocesoDisponibles,
    } = props;

    const esReclasificacion =
        tipoDestinoSeleccionado === TIPOS_DESTINO_BODEGA_PT.RECLASIFICACION;

    const esReproceso =
        tipoDestinoSeleccionado === TIPOS_DESTINO_BODEGA_PT.REPROCESO;

    const { getExistenciasBodega, getAlmacenesReproceso } = CreacionSalidaApi();

    const EliminarDetalleSalida = async (index) => {
        setDetallesSalida((prev) => prev.filter((_, i) => i !== index));
    };

    const {
        control: formControl,
        setValue: formSetValue,
        getValues: formGetValues,
        trigger: validateForm,
        watch: watchForm,
        formState: { errors },
    } = useForm({
        mode: "onChange",
    });

    const loadExistencias = useDebouncedAsyncSelect(function (search) {
        if (!tipoDestinoSeleccionado) return Promise.resolve([]);

        return getExistenciasBodega({
            search: search,
            tipo: tipoExistenciaSegunDestino(tipoDestinoSeleccionado),
        });
    });

    const loadAlmacenesReproceso = useDebouncedAsyncSelect(function (search) {
        return getAlmacenesReproceso({ search: search });
    });

    const AgregarDetalleSalida = async () => {
        const isValid = await validateForm();
        if (!isValid) return;

        const { existencia, pacas_consumo, tarjeta_salida, bodega_destino } =
            formGetValues();

        const pacas = Number(pacas_consumo);

        const etiquetaPallet = formatPalletLabel(
            existencia.numero_pallet,
            existencia.identificador_fisico,
        );

        // no permitir despachar más pacas de las disponibles en la existencia
        const existenciaPacasNumber = Number(existencia.pacas);
        if (pacas > existenciaPacasNumber) {
            NotificationManager.warning(
                `El pallet ${etiquetaPallet} solo tiene ${existenciaPacasNumber} pacas disponibles`,
                "Advertencia",
                3000,
            );
            return;
        }

        // evitar agregar dos veces la misma existencia (pallet + tipo)
        const yaAgregado = detallesSalida.some(
            (detalle) => detalle.existencia_id === existencia.id,
        );

        if (yaAgregado) {
            NotificationManager.warning(
                `El pallet ${etiquetaPallet} ya fue agregado`,
                "Advertencia",
                3000,
            );
            return;
        }

        // al enviar a reclasificación, las pacas no conformes pasan a
        // conformes: debe indicarse la tarjeta amarilla que respalda el cambio
        if (esReclasificacion && !tarjeta_salida) {
            NotificationManager.warning(
                "Debe indicar el número de tarjeta amarilla",
                "Advertencia",
                3000,
            );
            return;
        }

        // al enviar a reproceso, debe indicarse el almacén de reproceso
        // que recibirá las pacas de este pallet
        if (esReproceso && !bodega_destino) {
            NotificationManager.warning(
                "Debe seleccionar el almacén de reproceso destino",
                "Advertencia",
                3000,
            );
            return;
        }

        const nuevoDetalle = {
            existencia_id: existencia.id,
            pallet_id: existencia.pallet_id,
            numero_pallet: existencia.numero_pallet,
            identificador_fisico: existencia.identificador_fisico,
            producto: existencia.producto,
            es_conforme_bodega: existencia.es_conforme_bodega,
            pacas_disponibles: existencia.pacas,
            pacas: pacas,
            tarjeta_salida: esReclasificacion ? tarjeta_salida.trim() : null,
            bodega_destino_id: esReproceso ? bodega_destino.id : null,
            bodega_destino_nombre: esReproceso ? bodega_destino.nombre : null,
        };

        setDetallesSalida((prev) => [...prev, nuevoDetalle]);

        // Limpiar campos del formulario
        formSetValue("existencia", null);
        formSetValue("pacas_consumo", null);
        formSetValue("tarjeta_salida", null);
        formSetValue("bodega_destino", null);
    };

    const formValues = watchForm();

    useEffect(() => {
        formSetValue("existencia", null);
        formSetValue("pacas_consumo", null);
        formSetValue("tarjeta_salida", null);
        formSetValue("bodega_destino", null);
    }, [tipoDestinoSeleccionado]);

    const existenciaSeleccionada = formValues.existencia;
    const limitePacas = existenciaSeleccionada
        ? existenciaSeleccionada.pacas
        : 0;

    return (
        <React.Fragment>
            <div className="d-flex justify-content-between flex-wrap align-items-end mb-3">
                <h4 className="mb-0">Pacas a despachar</h4>
                <div className="formulario-fields-row w-100 gap-1rem flex-wrap">
                    <div
                        className="formulario-field"
                        style={{ minWidth: "150px" }}
                    >
                        <label className="text-nowrap">Pallet</label>
                        <Controller
                            name="existencia"
                            control={formControl}
                            rules={{
                                required: "Debe seleccionar un pallet",
                            }}
                            render={({ field }) => (
                                <AsyncSelect
                                    {...field}
                                    key={tipoDestinoSeleccionado || "none"}
                                    className="react-select-container"
                                    isSearchable
                                    isClearable
                                    defaultOptions
                                    loadOptions={loadExistencias}
                                    getOptionValue={(option) => option.id}
                                    getOptionLabel={(option) =>
                                        formatExistenciasLabel(
                                            option,
                                            // Las salidas a reproceso siempre usan pallets no conformes por eso se oculta
                                            !esReproceso,
                                        )
                                    }
                                    onChange={(selected) => {
                                        field.onChange(selected);

                                        // al elegir el pallet, sugerir por
                                        // default el almacén de reproceso que
                                        // corresponde a su padre PT
                                        if (esReproceso) {
                                            const almacenSugerido = selected
                                                ? almacenesReprocesoDisponibles.find(
                                                      (almacen) =>
                                                          almacen.padre_producto_terminado_id ===
                                                          selected.padre_producto_terminado_id,
                                                  )
                                                : null;

                                            formSetValue(
                                                "bodega_destino",
                                                almacenSugerido || null,
                                            );
                                        }
                                    }}
                                    isDisabled={!tipoDestinoSeleccionado}
                                    placeholder={
                                        !tipoDestinoSeleccionado
                                            ? "Seleccionar destino"
                                            : "Seleccionar"
                                    }
                                />
                            )}
                        />
                        {errors.existencia && (
                            <small className="text-danger">
                                {errors.existencia.message}
                            </small>
                        )}
                    </div>

                    <div className="formulario-field">
                        <label className="text-nowrap">Producto</label>
                        <input
                            type="text"
                            className="form-control"
                            disabled
                            value={
                                formValues.existencia &&
                                formValues.existencia.producto
                                    ? formValues.existencia.producto
                                    : ""
                            }
                        />
                    </div>

                    <div className="formulario-field">
                        <label className="text-nowrap">Pacas disponibles</label>
                        <RenderNumber
                            className="form-control disabled-input"
                            value={
                                formValues.existencia &&
                                formValues.existencia.pacas
                                    ? formValues.existencia.pacas
                                    : ""
                            }
                            decimalScale={0}
                        />
                    </div>

                    <div className="formulario-field">
                        <label className="text-nowrap">Producto conforme</label>
                        <input
                            type="text"
                            className="form-control"
                            disabled
                            value={
                                formValues.existencia
                                    ? formValues.existencia.es_conforme_bodega
                                        ? "Sí"
                                        : "No"
                                    : ""
                            }
                        />
                    </div>

                    <div className="formulario-field">
                        <label className="text-nowrap">Cantidad pacas</label>
                        <NumberField
                            control={formControl}
                            name="pacas_consumo"
                            errors={errors}
                            allowNegative={false}
                            rules={{
                                required: "Este campo es obligatorio",
                                min: {
                                    value: 1,
                                    message: "Debe ser al menos 1",
                                },
                                max: {
                                    value: limitePacas,
                                    message: `No puede superar las ${formatDisplayNumber(limitePacas)} pacas disponibles`,
                                },
                            }}
                            decimalScale={0}
                            disabled={!formValues.existencia}
                        />
                    </div>

                    {esReclasificacion && (
                        <div className="formulario-field">
                            <label className="text-nowrap">
                                No. tarjeta amarilla
                            </label>
                            <TextField
                                control={formControl}
                                name="tarjeta_salida"
                                errors={errors}
                                placeholder="Ejemplo: 003-145"
                                rules={{
                                    required:
                                        "Debe indicar el número de tarjeta",
                                    validate: (value) =>
                                        !value ||
                                        !!value.trim() ||
                                        "El número de tarjeta no puede contener solo espacios",
                                }}
                            />
                        </div>
                    )}

                    {esReproceso && (
                        <div
                            className="formulario-field"
                            style={{ minWidth: "180px" }}
                        >
                            <label className="text-nowrap">
                                Almacén de reproceso
                            </label>
                            <Controller
                                name="bodega_destino"
                                control={formControl}
                                rules={{
                                    required:
                                        "Debe seleccionar el almacén de reproceso",
                                }}
                                render={({ field }) => (
                                    <AsyncSelect
                                        {...field}
                                        className="react-select-container"
                                        isSearchable
                                        isClearable
                                        defaultOptions
                                        loadOptions={loadAlmacenesReproceso}
                                        getOptionValue={(option) => option.id}
                                        getOptionLabel={(option) =>
                                            option.nombre
                                        }
                                        onChange={(selected) => {
                                            field.onChange(selected);
                                        }}
                                        isDisabled={!formValues.existencia}
                                        placeholder={
                                            !formValues.existencia
                                                ? "Seleccionar pallet"
                                                : "Seleccionar"
                                        }
                                    />
                                )}
                            />
                            {errors.bodega_destino && (
                                <small className="text-danger">
                                    {errors.bodega_destino.message}
                                </small>
                            )}
                        </div>
                    )}

                    <div
                        className="formulario-field mb-0 ml-auto no-flex"
                        style={{ maxHeight: "57px" }}
                    >
                        <button
                            type="button"
                            className="btn btn-primary mt-auto"
                            onClick={AgregarDetalleSalida}
                        >
                            Agregar
                        </button>
                    </div>
                </div>
            </div>

            {/* TABLA DE DETALLES DE SALIDA */}
            <div className="new-table-wrapper creacion-produccion__table-wrapper">
                <table className="table-striped">
                    <thead>
                        <tr>
                            <th>ACCIONES</th>
                            <th>PALLET</th>
                            <th>PRODUCTO</th>
                            <th>CONFORME</th>
                            <th>PACAS</th>
                            {esReclasificacion && <th>TARJETA AMARILLA</th>}
                            {esReproceso && <th>ALMACÉN DE REPROCESO</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {detallesSalida.length === 0 && (
                            <tr>
                                <td
                                    className="text-center"
                                    colSpan={
                                        esReclasificacion || esReproceso ? 6 : 5
                                    }
                                >
                                    No se han agregado pacas a despachar
                                </td>
                            </tr>
                        )}
                        {detallesSalida.map((item, index) => {
                            return (
                                <tr key={index}>
                                    <td className="text-center text-nowrap">
                                        <div className="table-actions">
                                            <img
                                                src={eliminarIcon}
                                                alt="Eliminar"
                                                className="action_img"
                                                title="Eliminar"
                                                onClick={() =>
                                                    EliminarDetalleSalida(index)
                                                }
                                            />
                                        </div>
                                    </td>
                                    <td className="text-center text-nowrap">
                                        {formatPalletLabel(
                                            item.numero_pallet,
                                            item.identificador_fisico,
                                        )}
                                    </td>
                                    <td className="text-center text-nowrap">
                                        {item.producto}
                                    </td>
                                    <td className="text-center text-nowrap">
                                        {item.es_conforme_bodega ? "Sí" : "No"}
                                    </td>
                                    <td className="text-center text-nowrap">
                                        <RenderNumber
                                            value={item.pacas}
                                            decimalScale={0}
                                        />
                                    </td>
                                    {esReclasificacion && (
                                        <td className="text-center text-nowrap">
                                            <NullableValue
                                                value={item.tarjeta_salida}
                                            >
                                                <Badge
                                                    text={item.tarjeta_salida}
                                                    variant={"warning"}
                                                />
                                            </NullableValue>
                                        </td>
                                    )}
                                    {esReproceso && (
                                        <td className="text-center text-nowrap">
                                            <NullableValue
                                                value={
                                                    item.bodega_destino_nombre
                                                }
                                            >
                                                {item.bodega_destino_nombre}
                                            </NullableValue>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </React.Fragment>
    );
};
